import { Alert, Chip, Snackbar, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditNoteIcon from '@mui/icons-material/EditNote';

import ColorConstants from '../../../../defaults/ColorConstants.jsx';
import type { SummaryReportNotice, SummaryReportStatus } from '../../types.ts';

const { BlueAlien, WhiteAlien } = ColorConstants;

/** Long enough to read a name, short enough to stay out of the way. */
const TOAST_MS = 4000;

const noticeLineSx = { flex: 1, minWidth: 0 };

const brandedSeverities = new Set(['success', 'info']);

/** What the status chip says and looks like, per state. `none` has no chip. */
const STATUS_CHIPS = {
    clean: {
        label: 'Up to date',
        color: 'success' as const,
        icon: <CheckCircleOutlineIcon />,
        title: 'The view matches the saved report',
    },
    dirty: {
        label: 'Unsaved changes',
        color: 'warning' as const,
        icon: <EditNoteIcon />,
        title: 'This view has changed since it was loaded — Update to save it',
    },
};

export function SummaryReportsStatusLine({
    status,
    message,
}: {
    status: SummaryReportStatus;
    /** Takes the line when present. Nothing writes to it today. */
    message?: string;
}) {
    if (message) {
        return (
            <Typography
                variant='body2'
                color='text.secondary'
                noWrap
                // The panel is capped at a share of the header, and names are user-typed.
                title={message}
                sx={noticeLineSx}
            >
                {message}
            </Typography>
        );
    }

    // Nothing to be up to date with, so the crumb is dropped rather than filled with a
    // placeholder — the filter crumb beside it already carries the live fact.
    if (status === 'none') return null;

    const chip = STATUS_CHIPS[status];

    return (
        <Chip
            size='small'
            variant='outlined'
            color={chip.color}
            icon={chip.icon}
            label={chip.label}
            title={chip.title}
        />
    );
}

/** How many filters are shaping the data on screen. */
export function SummaryReportsFilterLine({ count }: { count: number }) {
    const label = count === 0 ? 'No filters' : count === 1 ? '1 filter' : `${count} filters`;

    return (
        <Typography
            variant='body2'
            color={count > 0 ? 'text.secondary' : 'text.disabled'}
            noWrap
            title={
                count > 0
                    ? `${label} shaping the summary data — set them in the filter panel`
                    : 'No filters — the summary covers every record'
            }
        >
            {label}
        </Typography>
    );
}

export function SummaryReportsNoticeToast({
    notice,
    open,
    onClose,
}: {
    notice: SummaryReportNotice | null;
    open: boolean;
    onClose: () => void;
}) {
    if (!notice) return null;

    const branded = brandedSeverities.has(notice.severity);

    return (
        <Snackbar
            key={notice.id}
            open={open}
            autoHideDuration={TOAST_MS}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            onClose={(_event, reason) => {
                if (reason !== 'clickaway') onClose();
            }}
        >
            <Alert
                severity={notice.severity}
                variant='filled'
                onClose={onClose}
                sx={{
                    width: '100%',
                    ...(branded && { backgroundColor: BlueAlien, color: WhiteAlien }),
                }}
            >
                {notice.message}
            </Alert>
        </Snackbar>
    );
}
